import { useState, useEffect, useMemo } from "react";
import "./App.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// ─── Imports: Constants
import {
  PAGE_SIZE,
  INIT_FILTERS,
  TABS,
} from "./constants";

// ─── Imports: Services & Utils
import { registerChartPlugins } from "./utils/chartPlugin";

// ─── Imports: Hooks
import { useStats, useChartData, usePaginatedData } from "./hooks/useStats";
import { useFilters, usePagination } from "./hooks/useFiltersAndPagination";
import useCurrencyPreference from "./hooks/useCurrencyPreference";

// ─── Imports: Components
import {
  StatsDisplay,
  SatelliteStatsDisplay,
} from "./components/sections/StatsDisplay";
import Galaxy from "./components/backgrounds/Galaxy";
import AuthShell from "./features/auth/AuthShell";
import ThemeToggle from "./features/auth/ThemeToggle";
import useAuthSession from "./features/auth/useAuthSession";
import useAppData from "./features/app-data/useAppData";
import { AppTitle, CompleteTabs } from "./features/app-shell/AppHeader";
import useAppPreferences from "./features/app-shell/useAppPreferences";
import ExportMenu from "./features/export/ExportMenu";
import useExportActions from "./features/export/useExportActions";
import PublicLanding from "./features/landing/PublicLanding";
import LegalPage from "./features/legal/LegalPage";
import {
  APP_ROUTES,
  getAuthModeFromRoute,
  getAuthRouteForMode,
} from "./features/navigation/appRoutes";
import { pushBrowserRoute } from "./features/navigation/browserRoute";
import useAppRoute from "./features/navigation/useAppRoute";
import OnboardingStepper from "./features/onboarding/OnboardingStepper";
import useOnboardingFlow from "./features/onboarding/useOnboardingFlow";
import DeleteConfirmationModal from "./components/common/DeleteConfirmationModal";
import RegistrosTab from "./features/complete/RegistrosTab";
import TorneiosTab from "./features/complete/TorneiosTab";
import GraficoTab from "./features/complete/GraficoTab";
import useCompleteModeActions from "./features/complete/useCompleteModeActions";
import SimplifiedMode from "./features/simplified/SimplifiedMode";

// ─── Register Chart.js Plugins & Scales
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);
registerChartPlugins();

// ─── Main App Component
export default function PokerTracker() {
  const {
    user,
    userId,
    accountFirstName,
    accountLastName,
    accountNeedsPasswordSetup,
    authLoading,
    authMode,
    authFirstName,
    authLastName,
    authPhone,
    authEmail,
    authPassword,
    authPasswordConfirm,
    authStatus,
    authDataStatus,
    signupConfirmationEmail,
    passwordResetEmail,
    passwordResetComplete,
    passwordRecoveryActive,
    setAuthMode,
    setAuthFirstName,
    setAuthLastName,
    setAuthPhone,
    setAuthEmail,
    setAuthPassword,
    setAuthPasswordConfirm,
    handleAuthSubmit,
    handleGoogleSignIn,
    handleAccountUpdate,
    handleAccountPasswordSetup,
    handleSignOut,
    getActiveUserId,
  } = useAuthSession();
  const currentRoute = useAppRoute({
    authLoading,
    user,
    passwordRecoveryActive,
  });
  const {
    dataLoaded,
    dataStatus,
    setDataStatus,
    tournaments,
    commonTournaments,
    refreshCommonTournaments,
    setTournaments,
    registros,
    setRegistros,
    completePreferences,
    setCompletePreferences,
    simplifiedBankroll,
    setSimplifiedBankroll,
  } = useAppData(userId);
  const tournamentsLoaded = dataLoaded;
  const registrosLoaded = dataLoaded;
  const { appMode, setAppMode, theme, toggleTheme } = useAppPreferences();
  const { displayCurrency, setDisplayCurrency } = useCurrencyPreference(userId);
  const selectableTournaments = useMemo(
    () => [...tournaments, ...commonTournaments],
    [commonTournaments, tournaments]
  );
  const { shouldShowOnboarding, completeOnboarding } = useOnboardingFlow({
    dataLoaded,
    userId,
    tournaments,
    registros,
    completePreferences,
    setCompletePreferences,
    simplifiedBankroll,
    setSimplifiedBankroll,
    setDisplayCurrency,
  });
  const currencyOptions = useMemo(
    () => ({ displayCurrency }),
    [displayCurrency]
  );
  const [tab, setTab] = useState("Registros");
  const [tabMotionDirection, setTabMotionDirection] = useState("forward");
  const [modeMotionDirection, setModeMotionDirection] = useState("forward");
  const handleTabChange = (nextTab) => {
    setTab((currentTab) => {
      const currentIndex = TABS.indexOf(currentTab);
      const nextIndex = TABS.indexOf(nextTab);
      setTabMotionDirection(nextIndex >= currentIndex ? "forward" : "back");
      return nextTab;
    });
  };
  const handleAppModeChange = (nextMode) => {
    setAppMode((currentMode) => {
      setModeMotionDirection(nextMode === "simple" ? "forward" : "back");
      return typeof nextMode === "function" ? nextMode(currentMode) : nextMode;
    });
  };

  // Filters & Pagination
  const {
    filters,
    setFilters,
    showFilters,
    setShowFilters,
    activeFilters,
    clearFilters,
  } = useFilters(INIT_FILTERS);
  const { page, setPage, resetPage } = usePagination();

  // Reset page when filters change
  useEffect(() => {
    resetPage();
  }, [filters, resetPage]);

  // ─── Stats calculations (custom hook)
  const { filtered, stats } = useStats(
    registros,
    selectableTournaments,
    filters,
    activeFilters,
    currencyOptions
  );

  // ─── Chart filter toggle
  const [showWithoutSat, setShowWithoutSat] = useState(false);

  // ─── Chart data
  const chartData = useChartData(
    filtered,
    registros,
    selectableTournaments,
    activeFilters,
    showWithoutSat,
    currencyOptions
  );

  // ─── Pagination
  const { totalPages, safePage, pageSlice } = usePaginatedData(
    filtered,
    page,
    PAGE_SIZE
  );

  const {
    tournamentForm,
    tournamentModal,
    registroForm,
    registroModal,
    getTournament,
    deleteTarget,
    setDeleteTarget,
    handleSaveTournament,
    handleEditTournament,
    handleDeleteTournament,
    handleSaveRegistro,
    handleEditRegistro,
    handleDeleteRegistro,
  } = useCompleteModeActions({
    tournaments,
    selectableTournaments,
    setTournaments,
    registros,
    setRegistros,
    setDataStatus,
    getActiveUserId,
  });

  const {
    exportStatus,
    sideMenuOpen,
    setSideMenuOpen,
    activeExportAction,
    setActiveExportAction,
    closeSideMenu,
    handleExportSpreadsheet,
  } = useExportActions({
    tournaments: selectableTournaments,
    registros,
    displayCurrency,
  });

  useEffect(() => {
    setSideMenuOpen(false);
    setActiveExportAction(null);
  }, [userId, setSideMenuOpen, setActiveExportAction]);

  const statusToast = dataStatus || authDataStatus || exportStatus;
  const isLegalRoute =
    currentRoute === APP_ROUTES.PRIVACY || currentRoute === APP_ROUTES.TERMS;
  useEffect(() => {
    if (authLoading || user || passwordRecoveryActive) return;
    if (authMode !== "signin" && authMode !== "signup") return;

    const routeAuthMode = getAuthModeFromRoute(currentRoute);
    if (!routeAuthMode || routeAuthMode === authMode) return;

    setAuthMode(routeAuthMode);
  }, [
    authLoading,
    authMode,
    currentRoute,
    passwordRecoveryActive,
    setAuthMode,
    user,
  ]);
  const authTitle =
    authMode === "signup"
      ? "Criar conta"
      : authMode === "reset"
        ? "Recuperar senha"
        : authMode === "recovery"
          ? "Redefinir senha"
          : "Entrar no orion33";
  const authShellProps = {
    firstName: authFirstName,
    lastName: authLastName,
    phone: authPhone,
    email: authEmail,
    password: authPassword,
    passwordConfirm: authPasswordConfirm,
    mode: authMode,
    status: authStatus,
    signupConfirmationEmail,
    passwordResetEmail,
    passwordResetComplete,
    loading: authLoading,
    onFirstNameChange: setAuthFirstName,
    onLastNameChange: setAuthLastName,
    onPhoneChange: setAuthPhone,
    onEmailChange: setAuthEmail,
    onPasswordChange: setAuthPassword,
    onPasswordConfirmChange: setAuthPasswordConfirm,
    onSubmit: handleAuthSubmit,
    onGoogleSignIn: handleGoogleSignIn,
  };
  const handleAuthRouteRequest = (nextMode) => {
    setAuthMode(nextMode);

    const nextRoute = getAuthRouteForMode(nextMode);
    pushBrowserRoute(nextRoute, { notify: true });
  };
  const handleLegalBack = () => {
    pushBrowserRoute(APP_ROUTES.ROOT, { notify: true });
  };

  // ─── Render
  if (isLegalRoute) {
    return (
      <div className="app-shell" data-theme={theme}>
        <LegalPage
          kind={currentRoute === APP_ROUTES.PRIVACY ? "privacy" : "terms"}
          onBack={handleLegalBack}
        />
      </div>
    );
  }

  if (authLoading && !user) {
    return (
      <div className="app-shell login-loading-screen" data-theme={theme}>
        <div className="login-loading">
          <div className="login-loading__mark" aria-hidden="true" />
          <div className="login-loading__text">Entrando</div>
        </div>
      </div>
    );
  }

  if (!user || passwordRecoveryActive) {
    if (currentRoute === APP_ROUTES.ROOT && !passwordRecoveryActive) {
      return (
        <div className="app-shell" data-theme={theme}>
          <PublicLanding
            theme={theme}
            onThemeToggle={toggleTheme}
            onAuthRouteRequest={handleAuthRouteRequest}
          />
        </div>
      );
    }

    return (
      <div className="app-shell" data-theme={theme}>
        <AuthShell
          title={authTitle}
          {...authShellProps}
          theme={theme}
          onModeChange={handleAuthRouteRequest}
          onThemeToggle={toggleTheme}
        />
      </div>
    );
  }

  return (
    <div
      className="app-shell"
      data-theme={theme}
    >
      <button
        className="menu-toggle"
        type="button"
        onClick={() => setSideMenuOpen(true)}
        aria-label="Abrir menu"
        title="Abrir menu"
      >
        <span />
        <span />
        <span />
      </button>

      <ExportMenu
        open={sideMenuOpen}
        activeAction={activeExportAction}
        userEmail={user.email}
        userFirstName={accountFirstName}
        userLastName={accountLastName}
        accountNeedsPasswordSetup={accountNeedsPasswordSetup}
        onClose={closeSideMenu}
        onActionToggle={(actionKey) =>
          setActiveExportAction((current) =>
            current === actionKey ? null : actionKey
          )
        }
        onSpreadsheetExport={handleExportSpreadsheet}
        onAccountUpdate={handleAccountUpdate}
        onAccountPasswordSetup={handleAccountPasswordSetup}
        onSignOut={handleSignOut}
      />
      <ThemeToggle theme={theme} onToggle={toggleTheme} />
      <OnboardingStepper
        open={shouldShowOnboarding}
        onComplete={completeOnboarding}
      />

      <div className="galaxy-backdrop">
        <Galaxy
          mouseRepulsion={false}
          mouseInteraction
          density={theme === "light" ? 0.18 : 0.13}
          glowIntensity={theme === "light" ? 0.16 : 0.08}
          saturation={86}
          hueShift={42}
          twinkleIntensity={theme === "light" ? 0.18 : 0.12}
          rotationSpeed={0.05}
          repulsionStrength={0}
          autoCenterRepulsion={0}
          starSpeed={0.3}
          speed={0.4}
          starOpacity={theme === "light" ? 2.2 : 1}
          starLightness={theme === "light" ? 38 : 72}
          compositeOperation={theme === "light" ? "source-over" : "lighter"}
        />
      </div>

      <div className={`app-content app-content--${appMode}`}>
        <AppTitle appMode={appMode} setAppMode={handleAppModeChange} />

        {appMode === "simple" ? (
          <div className={`motion-mode-panel motion-slide-${modeMotionDirection}`}>
            <SimplifiedMode
              bankroll={simplifiedBankroll}
              onChange={setSimplifiedBankroll}
              theme={theme}
              displayCurrency={displayCurrency}
            />
          </div>
        ) : (
          <div className={`motion-complete-flow motion-mode-panel motion-slide-${modeMotionDirection}`}>
        {/* ─── Filtered Indicator ─── */}
        {activeFilters && (
          <div className="filtered-indicator">
            <div className="filtered-indicator__line" />
            <span className="filtered-indicator__text">
              ⊞ estatísticas filtradas · {filtered.length} de {registros.length}{" "}
              registros
            </span>
            <div className="filtered-indicator__line" />
          </div>
        )}

        {/* ─── Stats Display ─── */}
        {tournamentsLoaded && registrosLoaded && (
          <>
            <StatsDisplay
              stats={stats}
              filteredCount={filtered.length}
              totalCount={registros.length}
              activeFilters={activeFilters}
              displayCurrency={displayCurrency}
              onDisplayCurrencyChange={setDisplayCurrency}
            />

            {stats.satTotal > 0 && (
              <SatelliteStatsDisplay
                stats={stats}
                displayCurrency={displayCurrency}
              />
            )}
          </>
        )}

        <CompleteTabs
          tab={tab}
          setTab={handleTabChange}
          tournamentModal={tournamentModal}
          registroModal={registroModal}
        />
        {/* ─── TAB: REGISTROS ─── */}
        {tab === "Registros" && (
          <div key="registros" className={`motion-tab-panel motion-slide-${tabMotionDirection}`}>
            <RegistrosTab
            registros={registros}
            tournaments={selectableTournaments}
            onRefreshCommonTournaments={refreshCommonTournaments}
            completePreferences={completePreferences}
            setCompletePreferences={setCompletePreferences}
            filtered={filtered}
            pageSlice={pageSlice}
            totalPages={totalPages}
            safePage={safePage}
            page={page}
            setPage={setPage}
            activeFilters={activeFilters}
            filters={filters}
            setFilters={setFilters}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            clearFilters={clearFilters}
            registroForm={registroForm}
            registroModal={registroModal}
            getTournament={getTournament}
            handleSaveRegistro={handleSaveRegistro}
            handleEditRegistro={handleEditRegistro}
            handleDeleteRegistro={handleDeleteRegistro}
            setDeleteTarget={setDeleteTarget}
            deleteTarget={deleteTarget}
            displayCurrency={displayCurrency}
            />
          </div>
        )}

        {/* ─── TAB: TORNEIOS ─── */}
        {tab === "Torneios" && (
          <div key="torneios" className={`motion-tab-panel motion-slide-${tabMotionDirection}`}>
            <TorneiosTab
            tournaments={tournaments}
            registros={registros}
            tournamentForm={tournamentForm}
            tournamentModal={tournamentModal}
            handleSaveTournament={handleSaveTournament}
            handleEditTournament={handleEditTournament}
            handleDeleteTournament={handleDeleteTournament}
            setDeleteTarget={setDeleteTarget}
            deleteTarget={deleteTarget}
            displayCurrency={displayCurrency}
            />
          </div>
        )}

        {/* ─── TAB: GRÁFICO ─── */}
        {tab === "Gráfico" && (
          <div key="grafico" className={`motion-tab-panel motion-slide-${tabMotionDirection}`}>
            <GraficoTab
            chartData={chartData}
            showWithoutSat={showWithoutSat}
            setShowWithoutSat={setShowWithoutSat}
            theme={theme}
            displayCurrency={displayCurrency}
            />
          </div>
        )}

        {/* ─── Delete Confirmation Modal ─── */}
        <DeleteConfirmationModal
          target={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirmTournament={handleDeleteTournament}
          onConfirmRegistro={handleDeleteRegistro}
        />

          </div>
        )}
      </div>

      {statusToast && (
        <div className="status-toast-region" aria-live="polite" aria-atomic="true">
          <div
            key={`${statusToast.type}-${statusToast.message}`}
            className={`status-toast ${statusToast.type}`}
          >
            {statusToast.message}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SubComponents
